import { useInlineApiErrors } from 'src/utils'

import { MAX_AUTO_RENEWS, QR_PANEL_STATUS } from '../constants'
import type { QrPanelVariant, QrSignInPanelProps } from '../types'
import { getTerminalStatus } from '../utils'

import { useQrAutoRenew } from './autoRenew'
import { useQrCountdown } from './countdown'
import { useQrWaitPolling } from './polling'
import { useQrManualRefresh } from './refresh'
import { useQrSessionLifecycle } from './session'
import { useQrTerminalNotice } from './terminal'

type UseQrSignInFlowParams = Pick<
  QrSignInPanelProps,
  'domainName' | 'onAuthenticated' | 'onSwitchToPassword'
> & {
  variant: QrPanelVariant
}

export const useQrSignInFlow = ({
  domainName,
  variant,
  onSwitchToPassword,
  onAuthenticated
}: UseQrSignInFlowParams) => {
  const { showApiErrors, errorAlert } = useInlineApiErrors()
  const {
    state,
    stateRef,
    setState,
    autoRenewCount,
    expiresAtMs,
    setExpiresAtMs,
    handledApprovalRef,
    renewTimeoutRef,
    refetchTimeoutRef,
    startSession,
    cancelCurrentSession
  } = useQrSessionLifecycle({ domainName, showApiErrors })

  useQrWaitPolling({
    state,
    stateRef,
    refetchTimeoutRef,
    handledApprovalRef,
    setState,
    setExpiresAtMs,
    showApiErrors,
    onAuthenticated
  })

  useQrAutoRenew({
    autoRenewCount,
    status: state.status,
    variant,
    renewTimeoutRef,
    startSession
  })

  useQrTerminalNotice(state.status)

  const { secondsRemaining, progressPercent } = useQrCountdown({
    state,
    expiresAtMs
  })

  const { isRefreshOverlayVisible, isRefreshPending, handleRefresh } =
    useQrManualRefresh({
      status: state.status,
      cancelCurrentSession,
      startSession
    })

  const handleCancel = async () => {
    await cancelCurrentSession()
    setState((current) => ({
      status: QR_PANEL_STATUS.CANCELED,
      session: current.session
    }))
  }

  const handleSwitchToPassword = async () => {
    await cancelCurrentSession()
    onSwitchToPassword?.()
  }

  const handleRetry = () => {
    startSession()
  }

  const isStarting = state.status === QR_PANEL_STATUS.STARTING
  const terminalStatus = getTerminalStatus(state.status)
  // The QR auto-renews on expiry a few times before stopping (to spare the
  // server). While it can still auto-renew, keep the manual refresh button
  // hidden — it only surfaces once auto-renew is exhausted (or the session
  // ended for another reason: denied / error).
  const isExpiredButAutoRenewing =
    state.status === QR_PANEL_STATUS.EXPIRED && autoRenewCount < MAX_AUTO_RENEWS
  const shouldShowCompactRefreshOverlay =
    variant === 'compact' &&
    (isRefreshOverlayVisible ||
      (Boolean(terminalStatus) && !isExpiredButAutoRenewing))

  return {
    state,
    secondsRemaining,
    progressPercent,
    isStarting,
    terminalStatus,
    shouldShowCompactRefreshOverlay,
    isRefreshPending,
    errorAlert,
    handleCancel,
    handleRefresh,
    handleRetry,
    handleSwitchToPassword
  }
}
